import { Controller } from "@hotwired/stimulus";

// Tracks blog engagement: scroll depth, reading time, and related post clicks
// Usage:
//   <article data-controller="blog-analytics"
//            data-blog-analytics-title-value="Blog Title"
//            data-blog-analytics-slug-value="blog-slug"
//            data-blog-analytics-category-value="article"
//            data-blog-analytics-locale-value="en">
export default class extends Controller {
  static values = {
    title: String,
    slug: String,
    category: String,
    locale: String
  };

  initialize() {
    this.scrollMilestones = [25, 50, 75, 100];
    this.reachedMilestones = new Set();
    this.startTime = Date.now();
    this.maxScrollPercent = 0;
  }

  connect() {
    this.trackPageView();
    this.boundHandleScroll = this.handleScroll.bind(this);
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);

    window.addEventListener("scroll", this.boundHandleScroll, { passive: true });
    document.addEventListener("visibilitychange", this.boundHandleVisibilityChange);
  }

  disconnect() {
    this.trackReadingTime();
    window.removeEventListener("scroll", this.boundHandleScroll);
    document.removeEventListener("visibilitychange", this.boundHandleVisibilityChange);
  }

  trackPageView() {
    if (typeof window.gtag !== "function") return;

    window.gtag("event", "blog_view", {
      blog_title: this.titleValue,
      blog_slug: this.slugValue,
      blog_category: this.categoryValue,
      blog_locale: this.localeValue
    });
  }

  handleScroll() {
    const scrollPercent = this.calculateScrollPercent();
    this.maxScrollPercent = Math.max(this.maxScrollPercent, scrollPercent);

    for (const milestone of this.scrollMilestones) {
      if (scrollPercent >= milestone && !this.reachedMilestones.has(milestone)) {
        this.reachedMilestones.add(milestone);
        this.trackScrollDepth(milestone);
      }
    }
  }

  calculateScrollPercent() {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY;

    if (documentHeight <= windowHeight) return 100;

    const scrollableHeight = documentHeight - windowHeight;
    return Math.round((scrollTop / scrollableHeight) * 100);
  }

  trackScrollDepth(percent) {
    if (typeof window.gtag !== "function") return;

    window.gtag("event", "blog_scroll", {
      blog_title: this.titleValue,
      blog_slug: this.slugValue,
      scroll_depth: percent
    });
  }

  handleVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this.trackReadingTime();
    }
  }

  trackReadingTime() {
    if (typeof window.gtag !== "function") return;

    const readingTimeSeconds = Math.round((Date.now() - this.startTime) / 1000);

    window.gtag("event", "blog_read_time", {
      blog_title: this.titleValue,
      blog_slug: this.slugValue,
      reading_time_seconds: readingTimeSeconds,
      max_scroll_percent: this.maxScrollPercent
    });
  }

  trackRelatedClick(event) {
    if (typeof window.gtag !== "function") return;

    const relatedTitle = event.currentTarget.dataset.blogTitle;
    const relatedSlug = event.currentTarget.dataset.blogSlug;

    window.gtag("event", "blog_related_click", {
      source_blog: this.slugValue,
      clicked_blog: relatedSlug,
      clicked_title: relatedTitle
    });
  }
}
